import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { CloudUpload } from "@mui/icons-material";
import { SubjectYearGroupPicker } from "../../question/components/SubjectYearGroupPicker";

const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
];

export const RagDocumentUploadDialog = ({
  open,
  onClose,
  onUploadSuccess,
  schoolId,
  parentDocument = null,
  preselectedSourceType = null,
}) => {
  const ApiClient = useApiClient();
  const fileInputRef = React.useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sourceType, setSourceType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [containsMultipleInstances, setContainsMultipleInstances] =
    useState(false);

  const [subject, setSubject] = useState(null);
  const [educationLevel, setEducationLevel] = useState(null);
  const [yearGroup, setYearGroup] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [selectedParent, setSelectedParent] = useState(parentDocument);
  const [eligibleParents, setEligibleParents] = useState([]);
  const [additionalContext, setAdditionalContext] = useState("");

  // Check if source type requires metadata
  const requiresMetadata = (type) => {
    return [
      "question",
      "mark scheme",
      "feedback",
      "overall feedback",
      "feedback framework",
    ].includes(type);
  };

  useEffect(() => {
    const fetchSourceTypes = async () => {
      try {
        const types = await ApiClient.rag.getSourceTypes();
        setSourceTypes(types || []);
      } catch (error) {
        console.error("Error fetching source types:", error);
      }
    };

    if (open) {
      fetchSourceTypes();
      // Set preselected source type if provided
      if (preselectedSourceType) {
        setSourceType(preselectedSourceType);
      }
      // Set parent document if provided
      if (parentDocument) {
        setSelectedParent(parentDocument);
      }
    }
  }, [open, preselectedSourceType, parentDocument]);

  // Fetch subjects and education levels when source type requires metadata
  useEffect(() => {
    const fetchSubjectsAndLevels = async () => {
      try {
        const response = await ApiClient.core.getSubjectsAndLevels();
        if (response) {
          setSubjects(response.subjects || []);
          setEducationLevels(response.educationLevels || []);
        }
      } catch (error) {
        console.error("Failed to fetch subjects and levels:", error);
      }
    };

    if (requiresMetadata(sourceType)) {
      fetchSubjectsAndLevels();
    }
  }, [sourceType]);

  // Update year groups when education level changes
  useEffect(() => {
    if (educationLevel) {
      setYearGroups(educationLevel.yearGroups || []);
    } else {
      setYearGroups([]);
      setYearGroup(null);
    }
  }, [educationLevel]);

  // Fetch eligible parent documents when source type changes
  useEffect(() => {
    const fetchEligibleParents = async () => {
      if (!sourceType || parentDocument) return;
      try {
        const parents =
          await ApiClient.rag.getEligibleParentDocuments(sourceType);
        setEligibleParents(parents || []);
      } catch (error) {
        console.error("Error fetching eligible parents:", error);
        setEligibleParents([]);
      }
    };

    fetchEligibleParents();
  }, [sourceType, parentDocument]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      enqueueSnackbar(
        `Invalid file type. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}`,
        { variant: "warning" },
      );
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (!file) return;

    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      enqueueSnackbar(
        `Invalid file type. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}`,
        { variant: "warning" },
      );
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !sourceType) return;

    setUploading(true);
    try {
      const uploadData = {
        file: selectedFile,
        sourceType,
        containsMultipleInstances,
      };

      // Add parent if selected
      if (selectedParent) {
        uploadData.parent = selectedParent.id;
      } else if (requiresMetadata(sourceType)) {
        uploadData.subject = subject?.id;
        uploadData.yearGroup = yearGroup?.id;
        uploadData.educationLevel = educationLevel?.id;
      }

      // Only for superuser uploads
      if (schoolId) {
        uploadData.school = schoolId;
      }

      // Add additional context for feedback framework
      if (sourceType === "feedback framework" && additionalContext) {
        uploadData.additionalContext = additionalContext;
      }

      const response = await ApiClient.rag.uploadDocument(uploadData);

      if (response.success) {
        enqueueSnackbar(response.message || "Document uploaded successfully", {
          variant: "success",
        });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        onUploadSuccess();
      } else {
        enqueueSnackbar(response.message || "Upload failed", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      enqueueSnackbar("Error uploading document", { variant: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setSourceType("");
      setSubject(null);
      setEducationLevel(null);
      setYearGroup(null);
      setSelectedParent(null);
      setEligibleParents([]);
      setAdditionalContext("");
      onClose();
    }
  };

  const formatSourceType = (type) => {
    return type
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Document</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload documents to personalise your Fruitify experience. Supported
          formats: PDF, DOCX, DOC, PNG, JPG, JPEG, WEBP.
        </Typography>

        {!preselectedSourceType && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="source-type-label">Document Type</InputLabel>
            <Select
              labelId="source-type-label"
              value={sourceType}
              label="Document Type"
              onChange={(e) => setSourceType(e.target.value)}
              disabled={uploading}
            >
              {sourceTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {formatSourceType(type)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {eligibleParents.length > 0 && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="parent-document-label">
              Link to Parent Document (Optional)
            </InputLabel>
            <Select
              labelId="parent-document-label"
              value={selectedParent?.id || ""}
              label="Link to Parent Document (Optional)"
              onChange={(e) => {
                const parent = eligibleParents.find(
                  (p) => p.id === e.target.value,
                );
                setSelectedParent(parent || null);
              }}
              disabled={uploading || !!parentDocument}
            >
              <MenuItem value="">
                <em>None - Upload as standalone document</em>
              </MenuItem>
              {eligibleParents.map((parent) => (
                <MenuItem key={parent.id} value={parent.id}>
                  {parent.originalFilename} (
                  {formatSourceType(parent.sourceType)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {parentDocument && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "primary.50", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This document will be linked to:
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {parentDocument.originalFilename} (
              {formatSourceType(parentDocument.sourceType)})
            </Typography>
          </Box>
        )}

        {!selectedParent && !parentDocument && requiresMetadata(sourceType) && (
          <>
            <Typography
              variant="body2"
              sx={{ mt: 2, mb: 1, color: "text.secondary" }}
            >
              Please provide extra information about the document
            </Typography>
            <SubjectYearGroupPicker
              subjects={subjects}
              educationLevels={educationLevels}
              yearGroups={yearGroups}
              selectedSubject={subject}
              setSelectedSubject={setSubject}
              selectedEducationLevel={educationLevel}
              setSelectedEducationLevel={setEducationLevel}
              selectedYearGroup={yearGroup}
              setSelectedYearGroup={setYearGroup}
              stackProps={{ width: "100%", mb: 3 }}
            />
            {sourceType === "feedback framework" && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Instructions (Optional)"
                placeholder="Provide specific guidance on how to use the framework when marking"
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                disabled={uploading}
                sx={{ mb: 3 }}
                helperText="Provide specific guidance on how to use the framework when marking"
              />
            )}
          </>
        )}

        {sourceType !== "feedback framework" && (
          <FormControlLabel
            control={
              <Checkbox
                checked={containsMultipleInstances}
                onChange={(e) => setContainsMultipleInstances(e.target.checked)}
                disabled={uploading}
              />
            }
            label="This document contains multiple source examples"
            sx={{ mb: 2 }}
          />
        )}

        <Box
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          sx={{
            border: "2px dashed",
            borderColor: dragging ? "primary.main" : "divider",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            backgroundColor: dragging ? "action.selected" : "transparent",
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CloudUpload sx={{ fontSize: 48, color: "primary.main" }} />
            <Typography variant="body1" color="text.secondary">
              Choose file or drag and drop here
            </Typography>
            <Button
              variant="outlined"
              component="label"
              disabled={uploading}
              startIcon={<CloudUpload />}
              onClick={(e) => e.stopPropagation()}
            >
              Choose File
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={ALLOWED_EXTENSIONS.join(",")}
                onChange={handleFileSelect}
                disabled={uploading}
                onClick={(e) => e.stopPropagation()}
              />
            </Button>
            {selectedFile && (
              <Typography variant="caption" color="text.secondary">
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Stack>
        </Box>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Uploading...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={
            !selectedFile ||
            !sourceType ||
            uploading ||
            (!parentDocument &&
              requiresMetadata(sourceType) &&
              (!subject || !yearGroup || !educationLevel))
          }
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RagDocumentUploadDialog;
